import React, {
    useState,
    useRef,
    ReactNode,
    useEffect,
    useCallback
} from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import ChevronLeftLarge from "@atlaskit/icon/glyph/chevron-left-large";
import { AtlassianIcon } from "@atlaskit/logo";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { WACAvatar } from "@/components/global-nav/user-profile-new/src/components/avatar";
import { useLocalizedHref } from "@/libraries/web-framework";
import {
    LogoV2 as Logo,
    ProductKeyTypes
} from "@/libraries/wpl/foundations/src";

import SearchAndProfile from "../nav-search-and-profile";
import SearchBar from "../search-bar";

import { CloseIcon } from "./icons/close";
import { MenuIcon } from "./icons/menu";
import { DropdownType } from "./mobile-nav";

const mobileLogoCss = css({
    display: "flex",
    alignItems: "center",
    [media.above.sm]: {
        display: "none"
    }
});

const tabletLogoCss = css({
    display: "none",
    [media.above.sm]: {
        "display": "flex",
        "alignItems": "center",

        ".productpage-subnav-atlas-icon": {
            "height": "68px",
            "width": "68px",
            "display": "flex",
            "alignItems": "center",
            "justifyContent": "center",
            "backgroundColor": "#0052CC",
            "a.navigation-bar-atlas-icon": {
                height: "56px",
                width: "56px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
            }
        }
    }
});

const productPageLogoCss = css({
    gap: "25px"
});

const logoCss = css({
    height: "21px"
});

const backButtonCss = css({
    color: token("color.text.selected"),
    display: "flex",
    alignItems: "center"
});

const chevronCss = css({
    marginBlock: "6px 0"
});

const profileAndMenuButtonCss = css({
    "display": "flex",
    "gap": token("space.200"),
    "alignContent": "center",

    ".search-and-profile": {
        "display": "flex",
        "flexDirection": "row",

        ".navigation-trynow": {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minWidth: "100px"
        },

        ".navigation-search": {
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
        },

        ".navigation-action": {
            "alignItems": "center",
            "display": "flex",
            "flexDirection": "row",
            "listStyle": "none",
            "margin": 0,
            "padding": 0,
            "> li": {
                alignItems: "center",
                display: "flex",
                flexDirection: "row",
                paddingInlineStart: "8px"
            }
        }
    },

    ".mobile-nav-user-profile": {
        display: "none"
    },

    ".mobile-nav-profile-avatar": {
        display: "block"
    },

    ".navigation-switcher-divider": {
        borderInlineStart: "1px solid #dfe1e5",
        width: "1px",
        height: "24px",
        marginInlineEnd: token("space.100")
    },

    [media.above.sm]: {
        ".mobile-nav-user-profile": {
            display: "flex",
            flexDirection: "row",
            alignItems: "center"
        },

        ".mobile-nav-profile-avatar": {
            display: "none"
        }
    }
});

const mobileIconContainerCss = css({
    height: "40px",
    display: "flex",
    alignItems: "center",
    [media.above.sm]: {
        display: "none"
    }
});

const tabletIconContainerCss = css({
    "display": "none",

    ".mobile-dropdown-menu": {
        display: "none"
    },

    [media.above.sm]: {
        "display": "flex",
        "alignItems": "center",
        "position": "relative",

        ".mobile-dropdown-menu": {
            "display": "block",
            "opacity": 1,
            "insetBlockStart": "100%",
            "minWidth": "380px",
            "width": "max-content",
            "position": "absolute",
            "height": "calc(100vh - 65px)",
            "inset": "0px 0px auto auto",
            "transform": "translate3d(-92%, 56px, 0px)",
            "boxSizing": "border-box",
            "overflow": "auto",
            "borderRadius": "20px",
            "border": "10px solid #fff",
            "boxShadow": "0 0 1px 0 #ccc, 0 5px 20px -5px rgba(0,0,0,0.1)",
            "order": 100,
            "backgroundColor": "#ffffff",
            "insetInlineStart": 0,
            "transition": "width 200ms ease",
            "zIndex": 10,
            "animation": "fadein 0.25s 1",
            ".inner-mobile-dropdown-menu": {
                display: "block",
                margin: "8px 16px"
            }
        }
    }
});

const profileCss = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
});

type MobileNavTopProps = {
    productKey: ProductKeyTypes;
    isLoggedIn: boolean;
    userName: string;
    picture: string;
    handleClickAvatar: () => void;
    showMenu: boolean;
    handleToggleMenu: (showMenu: boolean) => void;
    menuPage: DropdownType | string;
    handleMenuPage: (menuPage: DropdownType | string) => void;
    handleFirstOpen: (firstOpen: boolean) => void;
    mobileLogo?: JSX.Element;
    mobileLogoAriaLabel?: string;
    mobileLogoHref?: string;
    mobileLogoDataEventContainer?: string;
    isOpenSearch: boolean;
    setIsOpenSearch: React.Dispatch<React.SetStateAction<boolean>>;
    tabletDropdownMenu: ReactNode;
    isProductPage?: boolean;
    isShowTryNowLink?: boolean;
    navigationTrynowCtaText?: string;
    navigationTrynowCtaUrl?: string;
};

const DefaultAtlassianLogo = (
    <Logo
        brand="atlassian"
        type="logo"
        appearance="brand"
        height={20}
        width={156}
    />
);

export const MobileNavTop = ({
    productKey,
    isLoggedIn,
    userName,
    picture,
    handleClickAvatar,
    showMenu,
    handleToggleMenu,
    menuPage,
    handleMenuPage,
    handleFirstOpen,
    mobileLogo = DefaultAtlassianLogo,
    mobileLogoAriaLabel = "Atlassian logo",
    mobileLogoHref = "/",
    mobileLogoDataEventContainer = "globalNavWacV2",
    isOpenSearch,
    setIsOpenSearch,
    tabletDropdownMenu,
    isProductPage = false,
    isShowTryNowLink = false,
    navigationTrynowCtaText,
    navigationTrynowCtaUrl
}: MobileNavTopProps) => {
    const intl = useIntl();
    const searchAndProfileRef = useRef<HTMLDivElement>(null);
    const atlasUrl = useLocalizedHref("/");
    const [mobileDropdownMenu, setMobileDropdownMenu] =
        useState<HTMLElement | null>(null);

    const handleClickOutside = useCallback(
        (event: MouseEvent) => {
            const path = event.composedPath && event.composedPath();
            const style = window.getComputedStyle(
                mobileDropdownMenu as HTMLElement
            );
            const insideTabletMobileDropdownMenu =
                Array.prototype.includes.call(
                    Array.prototype.map.call(path, (el: { id: string }) => {
                        return (
                            el.id === "nav-tablet-mobile-dropdown-menu-button"
                        );
                    }),
                    true
                );

            if (!insideTabletMobileDropdownMenu && style.display !== "none") {
                handleToggleMenu(false);
                handleMenuPage("default");
                handleFirstOpen(false);
            }
        },
        [mobileDropdownMenu, handleToggleMenu, handleMenuPage, handleFirstOpen]
    );

    const handleKeydown = useCallback(
        (event: KeyboardEvent) => {
            if (event.code == "Escape" && showMenu) {
                handleToggleMenu(false);
                handleMenuPage("default");
                handleFirstOpen(false);
            }
        },
        [handleToggleMenu, handleMenuPage, handleFirstOpen, showMenu]
    );

    useEffect(() => {
        const element = document.getElementById(
            "nav-tablet-mobile-dropdown-menu-button"
        );
        setMobileDropdownMenu(element);
    }, []);

    useEffect(() => {
        document.addEventListener("click", handleClickOutside);
        document.addEventListener("keydown", handleKeydown);

        return () => {
            document.removeEventListener("click", handleClickOutside);
            document.removeEventListener("keydown", handleKeydown);
        };
    }, [handleClickOutside, handleKeydown]);

    return (
        <>
            <div css={mobileLogoCss} className="mobile-nav-company-logo">
                {menuPage === "default" || !showMenu ? (
                    <a
                        css={logoCss}
                        href={mobileLogoHref}
                        aria-label={mobileLogoAriaLabel}
                        data-event="clicked"
                        data-event-component="linkButton"
                        data-event-container={mobileLogoDataEventContainer}
                        data-product-key={productKey}
                    >
                        {mobileLogo}
                    </a>
                ) : (
                    <button
                        style={{ all: "unset", width: "100%" }}
                        onClick={(e) => {
                            e.preventDefault();
                            handleMenuPage("default");
                            handleFirstOpen(false);
                        }}
                    >
                        <div css={backButtonCss}>
                            <div css={chevronCss}>
                                <ChevronLeftLarge label="" />
                            </div>
                            <span>
                                {intl.formatMessage({
                                    id: "globalnavv2.menu-back",
                                    defaultMessage: "Back"
                                })}
                            </span>
                        </div>
                    </button>
                )}
            </div>
            <div
                css={[tabletLogoCss, isProductPage ? productPageLogoCss : ""]}
                className="tablet-nav-company-logo"
            >
                {isProductPage && (
                    <div className="productpage-subnav-atlas-icon">
                        <a
                            className="navigation-bar-atlas-icon"
                            href={atlasUrl}
                            aria-label={intl.formatMessage({
                                id: "productPageNav.logo-label",
                                defaultMessage: "Atlassian logo"
                            })}
                            data-event="clicked"
                            data-event-component="linkButton"
                            data-event-container="productPageSubNav"
                            data-product-key="home"
                        >
                            <AtlassianIcon size="large" appearance="inverse" />
                        </a>
                    </div>
                )}
                <a
                    css={logoCss}
                    href={mobileLogoHref}
                    aria-label={mobileLogoAriaLabel}
                    data-event="clicked"
                    data-event-component="linkButton"
                    data-event-container={mobileLogoDataEventContainer}
                    data-product-key={productKey}
                >
                    {mobileLogo}
                </a>
            </div>
            {isOpenSearch ? (
                <SearchBar setIsOpenSearch={setIsOpenSearch} />
            ) : (
                <div css={profileAndMenuButtonCss}>
                    <div className="mobile-nav-user-profile">
                        <SearchAndProfile
                            isShowTryNowLink={isShowTryNowLink}
                            productKey={productKey}
                            isShowSearchButton={true}
                            navigationTrynowCtaText={navigationTrynowCtaText}
                            navigationTrynowCtaUrl={navigationTrynowCtaUrl}
                            setIsOpenSearch={setIsOpenSearch}
                            searchAndProfileRef={searchAndProfileRef}
                            isDisplayDivider={false}
                        />
                        <div className="navigation-switcher-divider" />
                    </div>
                    {isLoggedIn && userName && (
                        <div
                            className="mobile-nav-profile-avatar"
                            css={profileCss}
                        >
                            <button
                                style={{ all: "unset" }}
                                aria-label={intl.formatMessage({
                                    id: "globalnavv2.menu-icon.toggle",
                                    defaultMessage: "Toggle Menu"
                                })}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleClickAvatar();
                                }}
                            >
                                <WACAvatar
                                    name={userName}
                                    src={picture}
                                    size={"medium"}
                                />
                            </button>
                        </div>
                    )}
                    {/* Mobile button */}
                    <div css={mobileIconContainerCss}>
                        <button
                            style={{ all: "unset" }}
                            aria-label={intl.formatMessage({
                                id: "globalnavv2.menu-icon.toggle",
                                defaultMessage: "Toggle Menu"
                            })}
                            className="globalnavV2ToggleMenu"
                            onClick={(e) => {
                                e.preventDefault();
                                handleToggleMenu(!showMenu);
                                handleMenuPage("default");
                                handleFirstOpen(!showMenu);
                            }}
                        >
                            {showMenu ? (
                                <CloseIcon
                                    label={intl.formatMessage({
                                        id: "globalnavv2.menu-icon.close",
                                        defaultMessage: "Close Menu"
                                    })}
                                />
                            ) : (
                                <MenuIcon
                                    label={intl.formatMessage({
                                        id: "globalnavv2.menu-icon.open",
                                        defaultMessage: "Open Menu"
                                    })}
                                />
                            )}
                        </button>
                    </div>
                    {/* Tablet button with dropdown */}
                    <div
                        css={tabletIconContainerCss}
                        id="nav-tablet-mobile-dropdown-menu-button"
                    >
                        <button
                            style={{
                                all: "unset",
                                width: "35px",
                                height: "35px"
                            }}
                            aria-label={intl.formatMessage({
                                id: "globalnavv2.menu-icon.toggle",
                                defaultMessage: "Toggle Menu"
                            })}
                            onClick={(e) => {
                                e.preventDefault();
                                handleToggleMenu(!showMenu);
                                handleMenuPage("default");
                                handleFirstOpen(!showMenu);
                            }}
                        >
                            {showMenu ? (
                                <CloseIcon
                                    label={intl.formatMessage({
                                        id: "globalnavv2.menu-icon.close",
                                        defaultMessage: "Close Menu"
                                    })}
                                />
                            ) : (
                                <MenuIcon
                                    label={intl.formatMessage({
                                        id: "globalnavv2.menu-icon.open",
                                        defaultMessage: "Open Menu"
                                    })}
                                />
                            )}
                        </button>
                        {showMenu && (
                            <div className="mobile-dropdown-menu">
                                {menuPage !== "default" && (
                                    <div
                                        css={css({
                                            marginBlockStart: "8px",
                                            marginBlockEnd: "8px",
                                            display: "flex",
                                            justifyContent: "flex-start",
                                            alignItems: "center"
                                        })}
                                    >
                                        <button
                                            style={{ all: "unset" }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleMenuPage("default");
                                                handleFirstOpen(false);
                                            }}
                                        >
                                            <div css={backButtonCss}>
                                                <div css={chevronCss}>
                                                    <ChevronLeftLarge label="" />
                                                </div>
                                                <span>
                                                    {intl.formatMessage({
                                                        id: "globalnavv2.menu-back",
                                                        defaultMessage: "Back"
                                                    })}
                                                </span>
                                            </div>
                                        </button>
                                    </div>
                                )}
                                {tabletDropdownMenu}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};
